import {gsap} from "gsap";

export class TimelineSprite extends PIXI.Sprite {

    public currentTweenFrame: number; // This value is only for tweening the currentFrame.
    protected _textures: PIXI.Texture[];
    private _sourceFPS: number;
    private _totalDuration: number;
    private _frameDuration: number;
    private _loopAnimation: TimelineLite;

    constructor(textures: PIXI.Texture[], sourceFPS: number = 30) {
        super(textures[0]);
        this._textures = textures;
        this._sourceFPS = sourceFPS;
        this._totalDuration = this._textures.length / this._sourceFPS;
        this._frameDuration = this._totalDuration / this._textures.length;
        this.currentTweenFrame = 0;
    }

    public static wrapIndex(length: number, index: number): number {
        if (length <= 0) {
            throw new Error(`Error: ArrayHelper.getValueInLoopRange(): length must bigger than zero!`);
        }

        if (index > (length - 1)) {
            return index % length;
        }

        if (index < 0) {
            return length - (Math.abs(index) % length);
        }

        return index;
    }

    public getAnimation(frames?: number[], onUpdateCallback?: () => void, onCompleteCallback?: () => void): gsap.core.Timeline {
        frames = frames != undefined ? frames : [0];
        if (frames.length === 1) {
            frames.push(this._textures.length - 1);
        }

        const timeline = new gsap.core.Timeline();
        timeline.add(() => this.setFrame(TimelineSprite.wrapIndex(this._textures.length, frames![0])));

        for (let i: number = 1; i < frames.length; i++) {
            const from = TimelineSprite.wrapIndex(this._textures.length, frames[i - 1]);
            const to = TimelineSprite.wrapIndex(this._textures.length, frames[i]);
            const duration = (Math.abs(from - to) + 1) * this._frameDuration;


            timeline.add(gsap.to(this, {
                duration: duration,
                currentTweenFrame: to + (to >= from ? 0.99 : -0.99), // +/-0.99 is for the last frame to play a "almost" full frame duration.
                ease: "none",
                onUpdate: () => {
                    this.applyFrame();
                    if (onUpdateCallback) {
                        onUpdateCallback()
                    }
                },
                onComplete: i === frames.length - 1 ? () => {
                    if (onCompleteCallback) {
                        onCompleteCallback();
                    }
                } : undefined
            }));
        }

        return timeline
    }

    public getAnimationAutoShowHide(autoShow: boolean = true, autoHide: boolean = true, frames?: number[], onUpdateCallback?: () => void, onCompleteCallback?: () => void): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        if (autoShow) {
            tl.add(() => this.show());
        }

        tl.add(this.getAnimation(frames, onUpdateCallback, () => {
            if (autoHide) {
                this.hide();
            }
            if (onCompleteCallback) {
                onCompleteCallback();
            }
        }));

        return tl;
    }

    /**
     * Sets the texture to be this frame. Should not be used during animation.
     * This is only for displaying a specific frame when not animating.
     * @param {number} frame
     */
    public setFrame(frame: number): void {
        this.currentTweenFrame = frame;
        this.applyFrame();
    }

    public playLoop(onUpdateCallback?: () => void): void {
        if (this._loopAnimation && this._loopAnimation.isActive()) {
            return;
        }

        this._loopAnimation = this.getAnimation([0], onUpdateCallback, () => this.playLoop(onUpdateCallback));
    }

    public stopLoop(): void {
        if (this._loopAnimation && this._loopAnimation.isActive()) {
            this._loopAnimation.pause();
            this._loopAnimation.kill();
        }
    }

    public show(): void {
        this.visible = true;
    }

    public hide(): void {
        this.visible = false;
    }

    private applyFrame(): void {
        this.texture = this._textures[this.currentFrame];
    }

    public get totalFrames(): number {
        return this._textures.length;
    }

    public get totalDuration(): number {
        return this._totalDuration;
    }

    public get frameDuration(): number {
        return this._frameDuration;
    }

    public get currentFrame(): number {
        const currFrame = Math.floor(this.currentTweenFrame);
        return Math.max(0, Math.min(currFrame, this._textures.length - 1));
    }
}